import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { useGetPopupApi } from '@/api/notice';
import dayjs from 'dayjs';

const Popup = () => {
	const { data: popupData, isLoading } = useGetPopupApi();
	const [visiblePopups, setVisiblePopups] = useState<number[]>([]);
	const [closedPopups, setClosedPopups] = useState<Set<string>>(new Set());

	useEffect(() => {
		if (!popupData?.popup) return;

		// localStorage에서 오늘 하루 보지 않기로 설정한 팝업 가져오기
		const today = dayjs().format('YYYY-MM-DD');
		const closedToday = JSON.parse(localStorage.getItem('closedPopups') || '{}');
		const closedTodaySet = new Set<string>();

		// 오늘 날짜로 닫힌 팝업만 필터링
		Object.keys(closedToday).forEach((popupPk) => {
			if (closedToday[popupPk] === today) {
				closedTodaySet.add(popupPk);
			}
		});

		setClosedPopups(closedTodaySet);

		// 현재 노출 가능한 팝업 필터링
		const now = dayjs();
		const validPopups = popupData.popup.filter((popup) => {
			// 오늘 하루 보지 않기로 설정한 팝업은 제외
			if (closedTodaySet.has(String(popup.popup_pk))) {
				return false;
			}

			// 노출 여부 확인
			if (popup.popup_exposure_yn === '미노출') {
				return false;
			}

			// 상시노출인 경우
			if (popup.popup_exposure_yn === '상시노출') {
				return true;
			}

			// 기간노출인 경우 날짜 확인
			if (popup.popup_exposure_yn === '기간노출') {
				const startDate = dayjs(popup.popup_exposure_start_date.split(' ')[0]);
				const endDate = dayjs(popup.popup_exposure_end_date.split(' ')[0]);
				const today = dayjs().format('YYYY-MM-DD');
				return today >= startDate.format('YYYY-MM-DD') && today <= endDate.format('YYYY-MM-DD');
			}

			return false;
		});

		setVisiblePopups(validPopups.map((p) => p.popup_pk as number));
	}, [popupData]);

	const handleClose = (popupPk: number | string, closeToday: boolean) => {
		if (closeToday) {
			// 오늘 하루 보지 않기
			const today = dayjs().format('YYYY-MM-DD');
			const closedPopups = JSON.parse(localStorage.getItem('closedPopups') || '{}');
			closedPopups[String(popupPk)] = today;
			localStorage.setItem('closedPopups', JSON.stringify(closedPopups));
			setClosedPopups((prev) => new Set(prev).add(String(popupPk)));
		}

		// 팝업 닫기
		setVisiblePopups((prev) => prev.filter((pk) => pk !== popupPk));
	};

	const handleImageClick = (url: string) => {
		if (url) {
			window.open(url, '_blank');
		}
	};

	if (isLoading || !popupData?.popup || visiblePopups.length === 0) {
		return null;
	}

	return (
		<>
			{popupData.popup
				.filter((popup) => visiblePopups.includes(popup.popup_pk as number))
				.map((popup, index) => (
					<Container key={popup.popup_pk} style={{ zIndex: 1000 + index }}>
						<PopupBox>
							<CloseButton onClick={() => handleClose(popup.popup_pk, false)}>×</CloseButton>
							<ImageContainer onClick={() => handleImageClick(popup.popup_url)}>
								<img src={popup.popup_image} alt={popup.popup_name} />
							</ImageContainer>
							<CloseTodayButton onClick={() => handleClose(popup.popup_pk, true)}>
								오늘 하루 보지 않기
							</CloseTodayButton>
						</PopupBox>
					</Container>
				))}
		</>
	);
};

export default Popup;

const Container = styled.div`
	position: fixed;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	background: rgba(0, 0, 0, 0.5);
	z-index: 1000;
	display: flex;
	align-items: center;
	justify-content: center;
`;

const PopupBox = styled.div`
	position: relative;
	background: #ffffff;
	border-radius: 0.8rem;
	box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.3);
	max-width: 90%;
	max-height: 90%;
	overflow: hidden;
`;

const CloseButton = styled.button`
	position: absolute;
	top: 1rem;
	right: 1rem;
	width: 3rem;
	height: 3rem;
	background: rgba(0, 0, 0, 0.5);
	border: none;
	border-radius: 50%;
	color: #ffffff;
	font-size: 2.4rem;
	line-height: 1;
	cursor: pointer;
	z-index: 10;
	display: flex;
	align-items: center;
	justify-content: center;
	transition: background 0.2s;

	&:hover {
		background: rgba(0, 0, 0, 0.7);
	}
`;

const ImageContainer = styled.div`
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;

	img {
		max-width: 100%;
		max-height: 70vh;
		object-fit: contain;
		display: block;
	}
`;

const CloseTodayButton = styled.button`
	width: 100%;
	padding: 1.2rem;
	background: #f5f5f5;
	border: none;
	border-top: 1px solid #e0e0e0;
	color: #666666;
	font-size: 1.4rem;
	cursor: pointer;
	transition: background 0.2s;

	&:hover {
		background: #e8e8e8;
	}
`;
